import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiDollarSign, FiPercent, FiTarget, FiTrendingUp, FiGlobe, FiClock, FiSave, FiLoader } from 'react-icons/fi';
import SafeIcon from './SafeIcon';
import { useRiskProfile } from '@/hooks/useRiskProfile';

const TRADING_STYLES = [
  { value: 'scalping', label: 'Scalping', desc: 'Ultra-short term (seconds to minutes)' },
  { value: 'day_trading', label: 'Day Trading', desc: 'Intraday positions only' },
  { value: 'swing', label: 'Swing Trading', desc: 'Multi-day to weeks' },
  { value: 'position', label: 'Position Trading', desc: 'Weeks to months' }
];

const MARKET_OPTIONS = [
  { value: 'gold', label: 'Gold (XAUUSD)' },
  { value: 'crypto', label: 'Cryptocurrencies' },
  { value: 'forex', label: 'Forex' },
  { value: 'indices', label: 'Indices' }
];

const SESSIONS = [
  { value: 'asia', label: 'Asian Session' },
  { value: 'london', label: 'London Session' },
  { value: 'new_york', label: 'New York Session' },
  { value: 'overlap', label: 'London/NY Overlap' }
];

const RiskProfileForm = ({ onSave, compact = false }) => {
  const { profile, loading, updateProfile } = useRiskProfile();
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  
  const [formData, setFormData] = useState({
    account_size: 10000,
    risk_per_trade_pct: 1,
    max_daily_loss_pct: 3,
    max_weekly_loss_pct: null,
    max_open_positions: 3,
    allowed_markets: ['gold', 'crypto', 'forex', 'indices'],
    trading_style: 'swing',
    session_preference: null
  });

  // Load profile data when available
  useEffect(() => {
    if (profile) {
      setFormData({
        account_size: profile.account_size || 10000,
        risk_per_trade_pct: profile.risk_per_trade_pct || 1,
        max_daily_loss_pct: profile.max_daily_loss_pct || 3,
        max_weekly_loss_pct: profile.max_weekly_loss_pct || null,
        max_open_positions: profile.max_open_positions || 3,
        allowed_markets: profile.allowed_markets || ['gold', 'crypto', 'forex', 'indices'],
        trading_style: profile.trading_style || 'swing',
        session_preference: profile.session_preference || null
      });
    }
  }, [profile]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMarketToggle = (market) => {
    setFormData(prev => {
      const current = prev.allowed_markets;
      if (current.includes(market)) {
        return { ...prev, allowed_markets: current.filter(m => m !== market) };
      }
      return { ...prev, allowed_markets: [...current, market] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatus(null);

    try {
      await updateProfile(formData);
      setStatus({ type: 'success', message: 'Risk profile saved successfully!' });
      onSave?.();
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'Failed to save profile' });
    } finally {
      setSaving(false);
    }
  };

  // Calculate derived values
  const riskAmount = (formData.account_size * formData.risk_per_trade_pct) / 100;
  const maxDailyLossAmount = (formData.account_size * formData.max_daily_loss_pct) / 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Status Message */}
      {status && (
        <div className={`p-3 rounded-xl text-sm ${
          status.type === 'success'
            ? 'bg-green-500/10 border border-green-500/30 text-green-400'
            : 'bg-red-500/10 border border-red-500/20 text-red-400'
        }`}>
          {status.message}
        </div>
      )}

      {/* Account Size */}
      <div>
        <label className="block text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
          <span className="p-1 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
            <SafeIcon icon={FiDollarSign} className="w-3 h-3 text-cyan-400" />
          </span>
          Trading Capital
        </label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
          <input
            type="number"
            min="100"
            step="100"
            value={formData.account_size}
            onChange={(e) => handleChange('account_size', parseFloat(e.target.value) || 0)}
            className="w-full bg-white/5 border border-white/10 rounded-xl py-3 pl-8 pr-4 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent transition-all"
            placeholder="10000"
          />
        </div>
      </div>

      {/* Risk Per Trade */}
      <div>
        <label className="block text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
          <span className="p-1 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
            <SafeIcon icon={FiPercent} className="w-3 h-3 text-cyan-400" />
          </span>
          Risk Per Trade
        </label>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min="0.25"
            max="5"
            step="0.25"
            value={formData.risk_per_trade_pct}
            onChange={(e) => handleChange('risk_per_trade_pct', parseFloat(e.target.value))}
            className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
          />
          <span className="text-white font-medium w-16 text-right">{formData.risk_per_trade_pct}%</span>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Risk per trade: <span className="text-cyan-400">${riskAmount.toLocaleString()}</span>
        </p>
      </div>

      {/* Max Daily Loss */}
      <div>
        <label className="block text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
          <span className="p-1 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
            <SafeIcon icon={FiTarget} className="w-3 h-3 text-cyan-400" />
          </span>
          Max Daily Loss
        </label>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min="1"
            max="10"
            step="0.5"
            value={formData.max_daily_loss_pct}
            onChange={(e) => handleChange('max_daily_loss_pct', parseFloat(e.target.value))}
            className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
          />
          <span className="text-white font-medium w-16 text-right">{formData.max_daily_loss_pct}%</span>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Daily stop: <span className="text-red-400">${maxDailyLossAmount.toLocaleString()}</span>
        </p>
      </div>

      {/* Max Open Positions */}
      <div>
        <label className="block text-sm font-medium text-gray-400 mb-2">Max Open Positions</label>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map(num => (
            <button
              key={num}
              type="button"
              onClick={() => handleChange('max_open_positions', num)}
              className={`flex-1 py-2 rounded-xl border transition-all ${
                formData.max_open_positions === num
                  ? 'bg-gray-900/80 border-cyan-500/50 text-white'
                  : 'bg-gray-900/60 border-purple-500/20 text-gray-400 hover:border-cyan-500/30'
              }`}
            >
              {num}
            </button>
          ))}
        </div>
      </div>

      {/* Trading Style */}
      <div>
        <label className="block text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
          <span className="p-1 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
            <SafeIcon icon={FiTrendingUp} className="w-3 h-3 text-cyan-400" />
          </span>
          Trading Style
        </label>
        <div className="grid grid-cols-2 gap-2">
          {TRADING_STYLES.map(style => (
            <button
              key={style.value}
              type="button"
              onClick={() => handleChange('trading_style', style.value)}
              className={`p-3 rounded-xl border text-left transition-all ${
                formData.trading_style === style.value
                  ? 'bg-gray-900/80 border-cyan-500/50'
                  : 'bg-gray-900/60 border-purple-500/20 hover:border-cyan-500/30'
              }`}
            >
              <div className="text-sm font-medium text-white">{style.label}</div>
              <div className="text-xs text-gray-500">{style.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Allowed Markets */}
      <div>
        <label className="block text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
          <span className="p-1 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
            <SafeIcon icon={FiGlobe} className="w-3 h-3 text-cyan-400" />
          </span>
          Allowed Markets
        </label>
        <div className="flex flex-wrap gap-2">
          {MARKET_OPTIONS.map(market => (
            <button
              key={market.value}
              type="button"
              onClick={() => handleMarketToggle(market.value)}
              className={`px-4 py-2 rounded-xl border transition-all ${
                formData.allowed_markets.includes(market.value)
                  ? 'bg-gray-900/80 border-cyan-500/50 text-white'
                  : 'bg-gray-900/60 border-purple-500/20 text-gray-400 hover:border-cyan-500/30'
              }`}
            >
              {market.label}
            </button>
          ))}
        </div>
      </div>

      {/* Session Preference */}
      <div>
        <label className="block text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
          <span className="p-1 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
            <SafeIcon icon={FiClock} className="w-3 h-3 text-cyan-400" />
          </span>
          Preferred Session (Optional)
        </label>
        <div className="grid grid-cols-2 gap-2">
          {SESSIONS.map(session => (
            <button
              key={session.value}
              type="button"
              onClick={() => handleChange('session_preference', 
                formData.session_preference === session.value ? null : session.value
              )}
              className={`p-3 rounded-xl border text-left transition-all ${
                formData.session_preference === session.value
                  ? 'bg-gray-900/80 border-cyan-500/50'
                  : 'bg-gray-900/60 border-purple-500/20 hover:border-cyan-500/30'
              }`}
            >
              <div className="text-sm text-white">{session.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <button
        type="submit"
        disabled={saving}
        className="w-full py-3 px-4 rounded-xl bg-gray-900/80 border border-cyan-500/30 text-white font-medium hover:border-cyan-500/50 hover:bg-gray-900/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {saving ? (
          <>
            <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <SafeIcon icon={FiSave} className="w-4 h-4" />
            Save Risk Profile
          </>
        )}
      </button>
    </form>
  );
};

export default RiskProfileForm;
